import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ListBox, Modal, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import type { VariantPicker } from '../../useSeller';
import { VariantPickerScope } from './VariantPickerScope';
import { VariantPickerOptionContent } from './VariantPickerOptionContent';
import { useVariantPickerFocus } from './useVariantPickerFocus';

type VariantPickerModalProps = {
    /** The open picker, or `null` to keep the modal closed. */
    picker: VariantPicker | null;
    onChoose: (variantId: string) => void;
    onClose: () => void;
};

/**
 * The variant picker a several-variant tile (or a scan, or a search) opens:
 * one row per variant with its label, price and sellable quantity, plus how
 * many the cart already holds. A variant that can't be chosen — nothing
 * sellable, or all of it already in the cart — is shown but disabled, with a
 * chip saying which, so "sold out" is an answer the seller can give.
 * Render-only — the rows, the variant it opens on and what choosing does come
 * from `useSeller`.
 *
 * Keyboard (bos-seller#53): it opens with focus on `initialVariantId`
 * (`useVariantPickerFocus`); the arrow keys move between the variants that
 * can be chosen, `Enter` chooses, `Esc` closes. `VariantPickerScope` suspends
 * the sale screen's shortcuts while it is open (`F1` stays live, like every
 * other overlay).
 */
export const VariantPickerModal: React.FC<VariantPickerModalProps> = ({
    picker,
    onChoose,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const initialOptionRef = useVariantPickerFocus(picker);

    return (
        <>
            {picker !== null ? <VariantPickerScope /> : null}
            <Modal.Backdrop
                isOpen={picker !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        onClose();
                    }
                }}
                variant="blur"
            >
                <Modal.Container placement="center" size="sm">
                    <Modal.Dialog
                        {...getNodeTestIdProp('seller-variant-picker')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Icon>
                                <Icon
                                    className="size-5"
                                    icon="solar:layers-bold-duotone"
                                />
                            </Modal.Icon>
                            <Modal.Heading>
                                {picker?.product.name}
                            </Modal.Heading>
                            <Typography color="muted" type="body-sm">
                                {t('seller.variantPicker.description')}
                            </Typography>
                        </Modal.Header>
                        <Modal.Body>
                            <ListBox
                                aria-label={t(
                                    'seller.variantPicker.description',
                                )}
                                disabledKeys={picker?.options
                                    .filter(
                                        (option) =>
                                            option.availability !== 'available',
                                    )
                                    .map((option) => option.variantId)}
                                onAction={(key) => onChoose(String(key))}
                                selectionMode="none"
                            >
                                {(picker?.options ?? []).map((option) => (
                                    <ListBox.Item
                                        id={option.variantId}
                                        key={option.variantId}
                                        ref={
                                            option.variantId ===
                                            picker?.initialVariantId
                                                ? initialOptionRef
                                                : undefined
                                        }
                                        textValue={option.label}
                                    >
                                        <VariantPickerOptionContent
                                            option={option}
                                        />
                                    </ListBox.Item>
                                ))}
                            </ListBox>
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </>
    );
};
